import CheckIcon from "@mui/icons-material/Check";
import {Box, Stack, SxProps, TextField, Theme} from "@mui/material";
import {alpha} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {surveyMarkdownToPlainText} from "../../shared/surveyMarkdownText.util";
import {getSurveyQuestionTypeFacts} from "../../shared/surveyQuestionTypeFacts";
import {RunnerQuestionProps} from "./runnerQuestion.types";
import {choiceSurfaceSx} from "./runnerSurface";
import {surveyControls} from "../surveyRunnerColors";

const OTHER_KEY = '__other__';

const OTHER_MAX_LENGTH = 10000;

const OTHER_COUNTER_FROM = Math.floor(OTHER_MAX_LENGTH * 0.9);

const INDICATOR_SIZE = 22;
const INDICATOR_ICON_SIZE = 16;
const PHOTO_BADGE_SIZE = 28;
const PHOTO_BADGE_ICON_SIZE = 18;

const formatCount = (count: number, language: string): string => {
    try {
        return count.toLocaleString(language);
    } catch {
        return String(count);
    }
};

function ChoiceQuestion({question, localize, language, value, onAnswerChange}: RunnerQuestionProps) {
    const {t} = useTranslation(undefined, {lng: language});

    const [localSelected, setLocalSelected] = useState<string[]>([]);
    const [localOtherText, setLocalOtherText] = useState('');

    const selectedFromValue = value && [
        ...(value.optionIds ?? []),
        ...(value.otherSelected ? [OTHER_KEY] : []),
    ];
    const selected = selectedFromValue ?? localSelected;
    const otherText = value?.customText ?? localOtherText;

    const multiple = !!question.allowMultiple;
    const pictures = question.type === SurveyQuestionType.PICTURE_CHOICE;
    const showOther = !!question.allowOther && !!getSurveyQuestionTypeFacts(question.type)?.otherOption;
    const otherPlaceholder = question.otherPlaceholder?.[language]?.trim() || t('Please specify');

    const groupLabel = surveyMarkdownToPlainText(localize(question.title)) || undefined;

    const toggle = (key: string) => {
        const next = selected.includes(key)
            ? selected.filter(entry => entry !== key)
            : (multiple ? [...selected, key] : [key]);

        const otherActive = next.includes(OTHER_KEY);

        setLocalSelected(next);
        if (!otherActive) setLocalOtherText('');

        onAnswerChange?.({
            ...value,
            optionIds: next.filter(entry => entry !== OTHER_KEY),
            otherSelected: otherActive,
            customText: otherActive ? otherText : undefined,
        });
    };

    const writeOther = (next: string) => {
        setLocalOtherText(next);
        onAnswerChange?.({...value, customText: next});
    };

    const indicator = (active: boolean) => (
        <Box
            aria-hidden
            sx={{
                flexShrink: 0,
                width: INDICATOR_SIZE,
                height: INDICATOR_SIZE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid',
                borderColor: theme => active ? surveyControls(theme).accent : surveyControls(theme).border,
                borderRadius: multiple ? 0.75 : '50%',
                bgcolor: active ? 'primary.main' : 'transparent',
                color: active ? 'primary.contrastText' : 'transparent',
            }}
        >
            {multiple
                ? <CheckIcon sx={{fontSize: INDICATOR_ICON_SIZE}}/>
                : <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: 'currentColor'}}/>}
        </Box>
    );

    const cardSx = (active: boolean): SxProps<Theme> => (theme: Theme) => ({
        ...choiceSurfaceSx(active)(theme),
        width: '100%',
        ...(pictures ? {p: 1.5} : {px: 2, py: 1.25}),
        textAlign: 'left',
        font: 'inherit',
        color: 'text.primary',
        cursor: 'pointer',
    });

    const optionCards = (question.options ?? []).map((option, index) => {
        const key = option.id ?? `option-${index}`;
        const active = selected.includes(key);

        return (
            <Box
                key={key}
                component={'button'}
                type={'button'}
                role={multiple ? undefined : 'radio'}
                aria-checked={multiple ? undefined : active}
                aria-pressed={multiple ? active : undefined}
                onClick={() => toggle(key)}
                sx={cardSx(active)}
            >
                {pictures && !!option.imageMedia?.mediaURI && (
                    <Box sx={{position: 'relative', mb: 1}}>
                        <Box
                            component={'img'}
                            src={option.imageMedia.mediaURI}
                            alt={''}
                            sx={{
                                display: 'block',
                                width: '100%',
                                aspectRatio: '4 / 3',
                                objectFit: 'cover',
                                borderRadius: 1,
                            }}
                        />
                        {active && (
                            <Box
                                aria-hidden
                                sx={theme => ({
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    width: PHOTO_BADGE_SIZE,
                                    height: PHOTO_BADGE_SIZE,
                                    display: 'grid',
                                    placeItems: 'center',
                                    borderRadius: '50%',
                                    bgcolor: 'primary.main',
                                    color: theme.palette.primary.contrastText,
                                    boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.35)}`,
                                })}
                            >
                                <CheckIcon sx={{fontSize: PHOTO_BADGE_ICON_SIZE}}/>
                            </Box>
                        )}
                    </Box>
                )}

                <Stack direction={'row'} alignItems={'center'} gap={1.5} sx={{minWidth: 0}}>
                    {indicator(active)}
                    <Typography variant={'body1'} sx={{minWidth: 0, wordBreak: 'break-word'}}>
                        {localize(option.label)}
                    </Typography>
                </Stack>
            </Box>
        );
    });

    if (pictures) {
        return (
            <Box
                role={multiple ? 'group' : 'radiogroup'}
                aria-label={groupLabel}
                sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: {
                        xs: 'repeat(2, minmax(0, 1fr))',
                        sm: 'repeat(auto-fill, minmax(180px, 1fr))',
                    },
                }}
            >
                {optionCards}
            </Box>
        );
    }

    const otherActive = selected.includes(OTHER_KEY);

    return (
        <Stack gap={1} role={multiple ? 'group' : 'radiogroup'} aria-label={groupLabel}>
            {optionCards}

            {showOther && (
                <Box>
                    <Box
                        component={'button'}
                        type={'button'}
                        role={multiple ? undefined : 'radio'}
                        aria-checked={multiple ? undefined : otherActive}
                        aria-pressed={multiple ? otherActive : undefined}
                        onClick={() => toggle(OTHER_KEY)}
                        sx={cardSx(otherActive)}
                    >
                        <Stack direction={'row'} alignItems={'center'} gap={1.5} sx={{minWidth: 0}}>
                            {indicator(otherActive)}
                            <Typography variant={'body1'}>{t('Other')}</Typography>
                        </Stack>
                    </Box>

                    {otherActive && (
                        <Box sx={{mt: 1}}>
                            <TextField
                                fullWidth
                                autoFocus
                                multiline
                                minRows={1}
                                maxRows={8}
                                value={otherText}
                                onChange={(event) => writeOther(event.target.value)}
                                placeholder={otherPlaceholder}
                                inputProps={{maxLength: OTHER_MAX_LENGTH}}
                            />

                            {otherText.length >= OTHER_COUNTER_FROM && (
                                <Typography
                                    variant={'body2'}
                                    color={'text.secondary'}
                                    sx={{mt: 0.5, textAlign: 'right'}}
                                >
                                    {`${formatCount(otherText.length, language)} / ${formatCount(OTHER_MAX_LENGTH, language)}`}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            )}
        </Stack>
    );
}

export default ChoiceQuestion;
